import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Copy, Trash2, Edit, Package, CheckSquare, Square, Camera } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, resolveImage, formatPrice, formatError } from "@/lib/api";
import { PhotoDialog } from "@/components/PhotoDialog";

const SEL = "h-10 rounded-xl border border-border bg-white px-3 text-xs font-bold text-slate-700";

const descendants = (cats, id) => {
  const out = new Set([id]);
  let frontier = [id];
  while (frontier.length) {
    const next = cats.filter((c) => frontier.includes(c.parent_id)).map((c) => c.id);
    next.forEach((n) => out.add(n));
    frontier = next;
  }
  return out;
};

const catLabel = (cats, c) => {
  const parent = cats.find((p) => p.id === c.parent_id);
  return parent ? `${parent.name} › ${c.name}` : c.name;
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [cats, setCats] = useState([]);
  const [catFilter, setCatFilter] = useState("");
  const [condFilter, setCondFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [photoProduct, setPhotoProduct] = useState(null);

  useEffect(() => {
    api.get("/categories", { params: { include_hidden: true } }).then((r) => setCats(r.data));
  }, []);

  const load = async () => {
    const { data } = await api.get("/products", { params: { include_drafts: true, limit: 5000, ...(q ? { q } : {}) } });
    setProducts(data);
  };

  useEffect(() => { load(); }, [q]);

  const catIds = catFilter ? descendants(cats, catFilter) : null;
  const isEbay = (p) => p.source === "ebay" || !!p.ebay_listing_id;
  const filtered = products.filter((p) =>
    (statusFilter === "all" || p.status === statusFilter) &&
    (!catIds || catIds.has(p.category_id)) &&
    (!condFilter || p.condition === condFilter) &&
    (!sourceFilter || (sourceFilter === "ebay" ? isEbay(p) : !isEbay(p))) &&
    (!stockFilter || (stockFilter === "out" ? Number(p.quantity) <= 0 : Number(p.quantity) > 0))
  );
  const filteredIds = filtered.map((p) => p.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));

  const toggleOne = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      if (allSelected) {
        const n = new Set(s);
        filteredIds.forEach((id) => n.delete(id));
        return n;
      }
      const n = new Set(s);
      filteredIds.forEach((id) => n.add(id));
      return n;
    });
  };
  const clearSel = () => setSelected(new Set());

  const bulkStatus = async (status) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const label = status === "published" ? "pubblicare" : "mettere in bozza";
    if (!window.confirm(`Vuoi ${label} ${ids.length} prodotto/i selezionato/i?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk-status", { ids, status });
      toast.success(`${data.updated} prodotto/i aggiornati`);
      clearSel();
      load();
    } catch (e) {
      toast.error("Errore aggiornamento", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setBulkBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il prodotto?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Prodotto eliminato");
    load();
  };

  const dup = async (id) => {
    await api.post(`/admin/products/${id}/duplicate`);
    toast.success("Prodotto duplicato (bozza)");
    load();
  };

  const quickPatch = async (id, updates) => {
    await api.patch(`/admin/products/${id}`, updates);
    load();
  };

  return (
    <AdminLayout title="Prodotti" back="/admin">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="admin-products-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per titolo, SKU o marca..."
            className="pl-10 h-11 rounded-xl"
          />
        </div>
        <div className="flex gap-2">
          {["all", "published", "draft"].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-4 h-11 rounded-xl text-xs font-bold border transition ${statusFilter === s ? "bg-brand-blue text-white border-brand-blue" : "bg-white border-border"}`}
            >
              {s === "all" ? "Tutti" : s === "published" ? "Pubblicati" : "Bozze"}
            </button>
          ))}
        </div>
        <Link
          to="/admin/prodotti/importa-ebay"
          data-testid="admin-import-ebay-btn"
          className="h-11 px-5 rounded-xl bg-brand-blue hover:bg-brand-blue-dark text-white font-bold inline-flex items-center gap-2 justify-center"
        >
          <Package className="w-5 h-5" strokeWidth={2.5} /> Importa da eBay
        </Link>
        <Link
          to="/admin/prodotti/nuovo"
          data-testid="admin-new-product-btn"
          className="h-11 px-5 rounded-xl bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-bold inline-flex items-center gap-2 justify-center"
        >
          <Plus className="w-5 h-5" strokeWidth={3} /> Nuovo prodotto
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-products-filters">
        <select data-testid="filter-category" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={SEL}>
          <option value="">Tutte le categorie</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{catLabel(cats, c)}</option>)}
        </select>
        <select data-testid="filter-condition" value={condFilter} onChange={(e) => setCondFilter(e.target.value)} className={SEL}>
          <option value="">Ogni condizione</option>
          <option value="Nuovo">Nuovo</option>
          <option value="Usato">Usato</option>
          <option value="Vintage">Vintage</option>
        </select>
        <select data-testid="filter-source" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className={SEL}>
          <option value="">Ogni origine</option>
          <option value="ebay">Importati da eBay</option>
          <option value="manual">Inseriti a mano</option>
        </select>
        <select data-testid="filter-stock" value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className={SEL}>
          <option value="">Ogni disponibilità</option>
          <option value="in">Disponibili</option>
          <option value="out">Esauriti</option>
        </select>
        <span className="self-center text-xs text-slate-500" data-testid="filtered-count">{filtered.length} prodotti</span>
      </div>

      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          data-testid="select-all-products"
          onClick={toggleAll}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-2 text-xs font-bold text-brand-blue disabled:opacity-40"
        >
          {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
          {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
        </button>
        <span className="text-xs text-slate-500" data-testid="selected-count">{selected.size} selezionati</span>
      </div>

      <div className="space-y-3">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="bg-white border border-border rounded-2xl p-3 flex flex-col sm:flex-row gap-3">
            <div className="flex gap-3 flex-1 min-w-0">
              <div className="pt-1">
                <Checkbox
                  data-testid={`sel-${p.id}`}
                  checked={selected.has(p.id)}
                  onCheckedChange={() => toggleOne(p.id)}
                />
              </div>
              <button
                type="button"
                data-testid={`photos-${p.id}`}
                onClick={() => setPhotoProduct(p)}
                title="Gestisci foto"
                className="relative w-16 h-16 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 group"
              >
                {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                <span className="absolute inset-0 bg-brand-blue/60 text-white flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[9px] font-bold">
                  <Camera className="w-4 h-4" /> {p.images?.length || 0} foto
                </span>
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold text-sm text-slate-900 truncate">{p.title}</span>
                  <Badge variant={p.status === "published" ? "default" : "secondary"} className="text-[10px]">
                    {p.status === "published" ? "Pubblicato" : "Bozza"}
                  </Badge>
                  {p.featured && <Badge className="bg-brand-yellow text-brand-blue text-[10px]">In evidenza</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {p.brand} · {p.condition} {p.sku ? `· ${p.sku}` : ""}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1 text-xs">
                    <span className="text-slate-500">Prezzo €</span>
                    <input
                      data-testid={`quick-price-${p.id}`}
                      type="number"
                      step="0.01"
                      defaultValue={p.price}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v !== p.price) quickPatch(p.id, { price: v });
                      }}
                      className="w-20 border border-border rounded-lg px-2 py-1 text-xs"
                    />
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <span className="text-slate-500">Offerta €</span>
                    <input
                      data-testid={`quick-sale-${p.id}`}
                      type="number"
                      step="0.01"
                      defaultValue={p.sale_price ?? ""}
                      onBlur={(e) => {
                        const raw = e.target.value;
                        const v = raw === "" ? null : parseFloat(raw);
                        quickPatch(p.id, { sale_price: v });
                      }}
                      className="w-20 border border-border rounded-lg px-2 py-1 text-xs"
                    />
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <span className="text-slate-500">Qta</span>
                    <input
                      data-testid={`quick-qty-${p.id}`}
                      type="number"
                      defaultValue={p.quantity}
                      onBlur={(e) => {
                        const v = parseInt(e.target.value);
                        if (!isNaN(v) && v !== p.quantity) quickPatch(p.id, { quantity: v });
                      }}
                      className="w-16 border border-border rounded-lg px-2 py-1 text-xs"
                    />
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <span className="text-slate-500">In evidenza</span>
                    <Switch
                      data-testid={`quick-featured-${p.id}`}
                      checked={!!p.featured}
                      onCheckedChange={(v) => quickPatch(p.id, { featured: v })}
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="flex sm:flex-col gap-2 justify-end">
              <Link to={`/admin/prodotti/${p.id}`} data-testid={`edit-${p.id}`} className="h-9 px-3 rounded-lg bg-brand-blue text-white text-xs font-bold inline-flex items-center gap-1">
                <Edit className="w-3.5 h-3.5" /> Modifica
              </Link>
              <button data-testid={`photos-btn-${p.id}`} onClick={() => setPhotoProduct(p)} className="h-9 px-3 rounded-lg bg-brand-yellow text-brand-blue text-xs font-bold inline-flex items-center gap-1">
                <Camera className="w-3.5 h-3.5" /> Foto
              </button>
              <button data-testid={`dup-${p.id}`} onClick={() => dup(p.id)} className="h-9 px-3 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold inline-flex items-center gap-1">
                <Copy className="w-3.5 h-3.5" /> Duplica
              </button>
              <button data-testid={`del-${p.id}`} onClick={() => del(p.id)} className="h-9 px-3 rounded-lg bg-red-500 text-white text-xs font-bold inline-flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Elimina
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center text-slate-500 py-12">Nessun prodotto trovato</div>
        )}
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-14 inset-x-0 z-30 border-t border-border bg-white/95 backdrop-blur-xl" data-testid="bulk-bar">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2 sm:gap-3">
            <span className="text-sm font-bold text-slate-700 flex-1">{selected.size} selezionati</span>
            <Button
              data-testid="bulk-publish-btn"
              onClick={() => bulkStatus("published")}
              disabled={bulkBusy}
              className="h-10 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-bold"
            >
              Pubblica selezionati
            </Button>
            <Button
              data-testid="bulk-draft-btn"
              onClick={() => bulkStatus("draft")}
              disabled={bulkBusy}
              variant="outline"
              className="h-10 rounded-full text-xs sm:text-sm font-bold"
            >
              Metti in bozza
            </Button>
            <button
              data-testid="bulk-clear-btn"
              onClick={clearSel}
              className="text-slate-400 hover:text-red-500 text-xs font-bold px-2"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
      <PhotoDialog product={photoProduct} onClose={() => setPhotoProduct(null)} onSaved={load} />
    </AdminLayout>
  );
}
